import { useState, useEffect } from "react";
import api from "../utils/api/axiosInstance";
import { mergeCategoryFieldDefs } from "../utils/categoryDisplayUtils";

const cache = new Map();
const inflight = new Map();

const EMPTY = { fields: [], allFields: [], name: null };

const toCacheEntry = (data = {}) => ({
  ...data,
  fields: data.fields || [],
  allFields: mergeCategoryFieldDefs(data),
  name: data.name || null,
});

export function invalidateCategoryDetails(categoryId) {
  if (categoryId) {
    cache.delete(categoryId);
    inflight.delete(categoryId);
    return;
  }
  cache.clear();
  inflight.clear();
}

export async function fetchCategoryDetails(categoryId, { signal, bypassCache } = {}) {
  if (!categoryId || categoryId === "unknown") {
    return EMPTY;
  }

  if (bypassCache) {
    cache.delete(categoryId);
    inflight.delete(categoryId);
  }

  if (cache.has(categoryId)) {
    return cache.get(categoryId);
  }

  if (inflight.has(categoryId)) {
    return inflight.get(categoryId);
  }

  const requestConfig = signal ? { signal } : undefined;
  const promise = api
    .get(`/categories/${categoryId}`, requestConfig)
    .then((res) => {
      const entry = toCacheEntry(res?.data ?? {});
      cache.set(categoryId, entry);
      inflight.delete(categoryId);
      return entry;
    })
    .catch((err) => {
      inflight.delete(categoryId);
      // Real failures (network error, 404, 500) must propagate so callers can show
      // a "Category Not Found" / error state instead of silently rendering an empty category.
      throw err;
    });

  inflight.set(categoryId, promise);
  return promise;
}

/**
 * Cached category fields + name. Dedupes concurrent requests per category ID.
 */
export function useCategoryDetails(categoryId) {
  const [details, setDetails] = useState(
    () => cache.get(categoryId) || EMPTY,
  );

  useEffect(() => {
    if (!categoryId || categoryId === "unknown") {
      setDetails(EMPTY);
      return;
    }

    const cached = cache.get(categoryId);
    if (cached) {
      setDetails(cached);
      return;
    }

    let cancelled = false;
    fetchCategoryDetails(categoryId)
      .then((entry) => {
        if (!cancelled) setDetails(entry);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        setDetails(EMPTY);
      });

    return () => {
      cancelled = true;
    };
  }, [categoryId]);

  return details;
}

export default useCategoryDetails;
